import { useState } from 'react';
import { Trophy, Flame, Target, MessageSquare, Users, Share2, Sparkles, Check, Copy } from 'lucide-react';
import { useFoundingMemberProgress } from '@/hooks/useFoundingMemberProgress';
import { useReferralCode } from '@/hooks/useReferralCode';
import { useDateFormat } from '@/contexts/DateFormatContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import type { FoundingMemberPath } from '@/types/founding-member';

const PATH_CONFIG: Record<
  FoundingMemberPath,
  { icon: typeof Flame; messages: { zero: string; low: string; mid: string; almost: string } }
> = {
  streak_30: {
    icon: Flame,
    messages: {
      zero: 'Start a streak to begin!',
      low: 'Great start! Keep going.',
      mid: 'Building real consistency!',
      almost: 'Almost there! {remaining} more days!',
    },
  },
  goals_3: {
    icon: Target,
    messages: {
      zero: 'Complete your first goal.',
      low: 'Nice work! {remaining} more to qualify.',
      mid: '{remaining} goal away from qualifying!',
      almost: '{remaining} goal away from qualifying!',
    },
  },
  feedback_10: {
    icon: MessageSquare,
    messages: {
      zero: 'Share your first feedback.',
      low: 'Keep sharing your thoughts!',
      mid: '{remaining} more submissions to go!',
      almost: '{remaining} more submissions to go!',
    },
  },
  referrals_5: {
    icon: Users,
    messages: {
      zero: 'Share your link to get started!',
      low: 'Great start! Keep sharing.',
      mid: '{remaining} more referrals to go!',
      almost: 'Just {remaining} more referral!',
    },
  },
};

function getContextualMessage(path: string, current: number, target: number): string {
  const config = PATH_CONFIG[path as keyof typeof PATH_CONFIG];
  if (!config) return '';

  const remaining = target - current;
  const pct = current / target;

  let msg: string;
  if (current === 0) msg = config.messages.zero;
  else if (pct < 0.4) msg = config.messages.low;
  else if (pct < 0.85) msg = config.messages.mid;
  else msg = config.messages.almost;

  return msg.replace('{remaining}', String(remaining));
}

function getQualificationPathLabel(path: FoundingMemberPath): string {
  switch (path) {
    case 'streak_30': return '30-day habit streak';
    case 'goals_3': return '3 goals completed';
    case 'feedback_10': return '10 feedback submissions';
    case 'referrals_5': return '5 referrals';
  }
}

interface FoundingMemberProgressWidgetProps {
  className?: string;
}

export function FoundingMemberProgressWidget({ className }: FoundingMemberProgressWidgetProps) {
  const { progress, thresholds, isQualified, qualificationPath, isLoading } = useFoundingMemberProgress();
  const { formatDate } = useDateFormat();

  if (isLoading || progress === null) return null;

  if (isQualified && qualificationPath) {
    return <CelebrationView qualificationPath={qualificationPath} qualifiedAt={progress.qualified_at} formatDate={formatDate} className={className} />;
  }

  // Find closest-to-completion path
  const closestPath = thresholds
    .reduce((best, t) => {
      const pct = t.current / t.target;
      const bestPct = best.current / best.target;
      return pct > bestPct ? t : best;
    });

  return (
    <Card className={cn('border-border/50 bg-card/50', className)}>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Trophy className="w-5 h-5 text-primary" />
          Founding Member Progress
        </CardTitle>
        <CardDescription>
          Earn lifetime free Pro by completing ANY path:
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {thresholds.map((t) => {
          const config = PATH_CONFIG[t.path as keyof typeof PATH_CONFIG];
          if (!config) return null;
          const Icon = config.icon;
          const pct = Math.min(Math.round((t.current / t.target) * 100), 100);
          const isClosest = t.path === closestPath.path && t.current > 0;

          return (
            <div
              key={t.path}
              className={cn(
                'rounded-lg p-3 space-y-2 transition-colors',
                isClosest && 'bg-primary/5 ring-1 ring-primary/20',
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-foreground" />
                  <span className="text-sm font-medium">
                    {t.label}
                  </span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {t.current}/{t.target}
                </span>
              </div>
              <Progress value={pct} className="h-2" />
              <p className="text-xs text-muted-foreground">
                {getContextualMessage(t.path, t.current, t.target)}
              </p>
            </div>
          );
        })}

        <CopyReferralButton />
      </CardContent>
    </Card>
  );
}

function CopyReferralButton() {
  const { data: referralCode, isLoading } = useReferralCode();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!referralCode) return;
    const url = `${window.location.origin}/welcome?ref=${referralCode}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast({ title: 'Copied!', description: 'Referral link copied to clipboard.' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Failed to copy', description: 'Please copy the link manually.', variant: 'destructive' });
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      className="w-full gap-2"
      onClick={handleCopy}
      disabled={isLoading || !referralCode}
    >
      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      {copied ? 'Copied!' : 'Copy Referral Link'}
    </Button>
  );
}

function CelebrationView({
  qualificationPath,
  qualifiedAt,
  formatDate,
  className,
}: {
  qualificationPath: FoundingMemberPath;
  qualifiedAt: string | null;
  formatDate: (date: Date | string, style?: string) => string;
  className?: string;
}) {
  const handleShare = async () => {
    const shareData = {
      title: 'Founding Member - MomentumEQ',
      text: "I earned Founding Member status on MomentumEQ — lifetime free Pro access! Check it out:",
      url: window.location.origin,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // User cancelled or share failed — no-op
      }
    } else {
      const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareData.url)}`;
      window.open(linkedInUrl, '_blank', 'width=600,height=400');
    }
  };

  return (
    <Card className={cn('border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-green-500/5', className)}>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-yellow-500" />
          You're a Founding Member!
        </CardTitle>
        <CardDescription>
          Qualified via {getQualificationPathLabel(qualificationPath)}
          {qualifiedAt && <> on {formatDate(new Date(qualifiedAt), 'short')}</>}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          You have lifetime free access to Pro features.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={handleShare}
          data-testid="share-achievement-button"
        >
          <Share2 className="w-4 h-4" />
          Share Your Achievement
        </Button>
      </CardContent>
    </Card>
  );
}
